import { isLocalTerminationDraft } from './termination-types'
/**
 * Processes (EDO) view-model mapping — turns live `/edo/*` requests & tasks into
 * the demo-`Process` shape the UI renders. Shared by the Processes screen and the
 * Home "Все процессы" card so both show an identical list (single source of truth).
 */
import type { Lang, Localized } from '../../i18n'
import { AZ_DB } from '../../i18n/az-data'
import { fmtNum, fold, timeAgo, ymd10 } from '../../lib/format'
import { CONTRACT_ENUM_LABEL, EDO_TYPE_LABEL } from '../../lib/edo-labels'
import { allLangs, typeOf } from './registry'
import { periodLabel } from './itWorks/itWorksRules'
import type { Process, ProcStatus, RouteStep } from './registry'
import type { BusinessTripPayload, EdoRequest, EdoRouteStep, EdoTask, EdoType, EdoStage, EdoStatus, EdoParticipantRole, RequestCard, TripDestination } from '../../lib/types'

/** ProcVM extends the registry `Process` shape with the live ids/flags needed for actions. */
export type ProcVM = Process & {
  live?: boolean
  /** EdoRequest.number — сквозной номер документа; у демо-строк его нет (там id = 'DOC-1042'). */
  docNumber?: number
  /** EdoRequest.portalRequestId — номер в формате 1С (ITO-2026-000128); показывает docNo() у IT_WORKS. */
  portalRequestId?: string | null
  reqId?: string
  taskId?: string
  stage?: EdoStage
  canRemind?: boolean
  canDecide?: boolean
  /** Мои роли в документе — только на вкладке «С моим участием» (инициатор/согласующий/подписант). */
  myRoles?: EdoParticipantRole[]
  /** Конкретное наименование документа (вид отпуска / EDO_TYPE_LABEL) — заголовок карточки; см. titleOf(). */
  title?: Localized
  /** Заявка подана «от имени» (роль proxy): ФИО сотрудника-инициатора — пометка в списке. */
  employmentCompany?: string
  onBehalfOf?: string
  /** ФИО автора (инициатора) документа — в строке списка под названием. */
  author?: string
  /** Договор: компания-сторона с нашей стороны (payload.company) — пилюля в строке списка. */
  company?: Localized
  /** Договор: наименование контрагента (payload.counterpartyName). */
  counterparty?: string
  /** Подпись пилюли, когда укрупнённой мало: «На доработке» вместо общего «В работе». Фильтр — по status. */
  statusLabel?: Localized
  /** Current termination contract has no implemented approval route yet. */
  approvalRouteUnavailable?: boolean
}

/** Моя роль в документе → метка на строке списка (см. rolesOf). */
export const PARTICIPANT_ROLE_LABEL: Record<EdoParticipantRole, Localized> = {
  initiator: { ru: 'Вы автор', en: 'You are the author', az: 'Siz müəllifsiniz', ka: 'თქვენ ხართ ავტორი' },
  submitter: { ru: 'Вы подали от имени', en: 'You submitted on behalf', az: 'Siz adından təqdim etdiniz', ka: 'თქვენ წარადგინეთ სახელით' },
  approver: { ru: 'Вы согласующий', en: 'You are an approver', az: 'Siz razılaşdıransınız', ka: 'თქვენ ხართ შემთანხმებელი' },
  signer: { ru: 'Вы подписант', en: 'You are the signer', az: 'Siz imzalayansınız', ka: 'თქვენ ხართ ხელმომწერი' },
}

/**
 * EdoType с бэка → id плитки реестра (иконка/тон/название). Промах маппинга (новый тип, которого
 * фронт ещё не знает) уходит в 'unknown' и рисуется нейтральным «Документом» — раньше падал
 * в 'vac' и чужая заявка выглядела «Отпуском».
 */
const TYPE_OF: Record<EdoType, string> = {
  VACATION: 'vac',
  TIME_OFF: 'off',
  HIRE: 'hire',
  TERMINATION: 'fire',
  STUDY_LEAVE: 'vac', // учебный отпуск — рисуем как отпуск (иконка/тон)
  BUSINESS_TRIP: 'trip',
  RECRUITMENT: 'recruitment',
  SICK_LEAVE: 'off', // больничный — рисуем как отгул (иконка/тон)
  CONTRACT: 'contract', // договор (ТЗ-2026-015) — своя плитка
  // «Семейные» отпуска (ТЗ-2026-012/013/014) — свои плитки в CreateTab, в списке рисуем как отпуск.
  PATERNITY_LEAVE: 'vac',
  MATERNITY_LEAVE: 'vac',
  CHILD_CARE_LEAVE: 'vac',
  FREEFORM: 'freeform', // своя плитка: типовой формы нет, маршрут собирает инициатор
  IT_WORKS: 'it_works', // IT-работы (ТЗ-2026-АКТ001) — своя плитка в разделе «Общие»
}

const STATUS_OF = (s: EdoStatus): ProcStatus => {
  switch (s) {
    case 'APPROVED':
    case 'POSTED':
    case 'CLOSED': // подбор: вакансия закрыта (кандидат найден)
    case 'REGISTERED': // больничный: зарегистрирован (терминальный успех)
    case 'AWAITING_TERMINATION_POSTING': // увольнение «Завершён»: приказ подписан, скан загружен
      return 'done'
    case 'REJECTED':
    case 'POST_FAILED':
      return 'rejected'
    case 'CANCELLED':
      // Отмена — решение инициатора/HR, не отказ и не сбой: свой нейтральный визуал
      // (раньше маппилась в rejected и строка рисовала красное «Ошибка проводки»).
      return 'cancelled'
    case 'REWORK':
    case 'AWAITING_SIGNED_COPY': // договор у автора: скан подписанного экземпляра
    case 'POSTING': // договор передаётся в 1С
    case 'AWAITING_HIRE_POSTING':
    case 'IN_PROGRESS': // подбор: «В работе» (старт поиска)
    case 'SEARCHING': // подбор: «Поиск ведётся»
      return 'work'
    default:
      return 'review' // DRAFT, MGR_REVIEW, SUP_REVIEW, HR_REVIEW, SIGNING, CEO_REVIEW, NOT_REVIEWED
  }
}

export const STAGE_ACT: Record<EdoStage, Localized> = {
  MANAGER: { ru: 'Согласование руководителем', en: 'Manager approval' },
  SUPERVISOR: { ru: 'Согласование рук. СП', en: 'Supervisor approval' },
  HR: { ru: 'Утверждение HR', en: 'HR approval' },
  SIGNATURE: { ru: 'Подпись', en: 'Signature' },
  ACCOUNTANT: { ru: 'Начисление (бухгалтер)', en: 'Accrual (accountant)' },
  // Командировка (ТЗ-2026-003 v1.2)
  TRAVEL: { ru: 'Билеты, бронь и расходы (HR)', en: 'Tickets, booking, expenses (HR)' },
  EXPENSE_REVIEW: { ru: 'Примерный расчёт расходов', en: 'Preliminary expense calculation' },
  ORDER: { ru: 'Печать, подпись ГД и скан приказа (HR)', en: 'Order print, GD signature, scan (HR)' },
  CFO: { ru: 'Согласование начисления (CFO)', en: 'Accrual approval (CFO)' },
  ACCRUAL: { ru: 'Начисление командировочных', en: 'Per-diem accrual' },
  REWORK: { ru: 'Доработка', en: 'Rework' },
  // Подбор персонала (ТЗ-2026-011)
  CEO: { ru: 'Согласование ГД', en: 'CEO approval' },
  SEARCH: { ru: 'Старт поиска (ОИ/HR)', en: 'Search start (HR)' },
  CLOSE: { ru: 'Закрытие с кандидатом (ОИ/HR)', en: 'Close with candidate (HR)' },
  // Уход за ребёнком (ТЗ-2026-013 §4.2): ручное уведомление госфонда бухгалтером.
  DSMF: { ru: 'Уведомление в DSMF', en: 'DSMF notification' },
  // Произвольный документ: согласующего выбрал инициатор — роли/должности у шага нет.
  CUSTOM: { ru: 'Согласование', en: 'Approval' },
  // Договор (23.09.2026): скан подписанного экземпляра и срок действия вносит автор.
  SIGNED_COPY: { ru: 'Подписанный экземпляр договора', en: 'Signed contract copy', az: 'Müqavilənin imzalanmış nüsxəsi' },
  // Увольнение (ТЗ-2026-008 шаг 7): интервью проводит HR, проверивший заявку.
  EXIT_INTERVIEW: { ru: 'Exit-интервью (HR)', en: 'Exit interview (HR)', az: 'Exit-müsahibə (HR)' },
  // IT-работы (ТЗ-2026-АКТ001): согласующие строк модулей, затем финальный утверждающий.
  IT_MODULES: { ru: 'Согласование модулей', en: 'Module approval', az: 'Modulların razılaşdırılması' },
  IT_FINAL: { ru: 'Финальное утверждение', en: 'Final approval', az: 'Yekun təsdiq' },
}

/** Групповая задача без персонального адресата → человекочитаемое имя группы. */
export const GROUP_LABEL: Record<string, Localized> = {
  manager: { ru: 'Руководитель', en: 'Manager' },
  supervisor: { ru: 'Руководитель СП', en: 'Supervisor' },
  hr: { ru: 'Отдел кадров (HR)', en: 'HR' },
  gd: { ru: 'Генеральный директор', en: 'General Director' },
  cfo: { ru: 'Финансовый директор (CFO)', en: 'CFO' },
  finance: { ru: 'Финансовый отдел', en: 'Finance' },
  accountant: { ru: 'Бухгалтерия', en: 'Accounting' },
  initiator: { ru: 'Инициатор', en: 'Initiator' },
  // Фолбэк шага произвольного документа — в норме там всегда конкретный человек из маршрута.
  approver: { ru: 'Согласующий', en: 'Approver' },
  // IT-работы: шаги личные, группа — только фолбэк, пока имени нет (будущий финал — см. cardRoute).
  it_modules: { ru: 'Согласующие модулей', en: 'Module approvers', az: 'Modul razılaşdıranları' },
  it_final: { ru: 'Финальный утверждающий', en: 'Final approver', az: 'Yekun təsdiqləyən' },
}

const TASK_STATE: Record<string, RouteStep['state']> = {
  OPEN: 'current',
  APPROVED: 'done',
  RESUBMITTED: 'done',
  REJECTED: 'rejected',
  REWORK: 'rejected',
  // Задача снята вместе с отзывом заявки — она уже не состоится («Ожидает» тут вводило в заблуждение).
  CANCELLED: 'na',
  ESCALATED: 'skipped', // прервана таймером 3 ч — «не рассмотрено», процесс ушёл дальше
}

export const VAC_KIND: Record<string, { ru: string; en: string }> = {
  basic: { ru: 'Основной отпуск', en: 'Annual leave' },
  unpaid: { ru: 'За свой счёт', en: 'Unpaid leave' },
  study: { ru: 'Учебный отпуск', en: 'Study leave' },
}

/** Local HH:MM from an ISO timestamp (UTC → device local for display). */
const hhmm = (v?: string | null) => {
  if (!v) return ''
  const d = new Date(String(v))
  return isNaN(d.getTime()) ? '' : d.toTimeString().slice(0, 5)
}

/**
 * Перевод слова-метки для az-варианта меты: даты/имена/города остаются как есть,
 * а переводимые токены (вид отпуска, причина, грейд, подразделение, тип) берутся из
 * словаря по русскому ключу. Нет в словаре → слово остаётся русским (безопасный откат).
 */
const azw = (ru: string) => AZ_DB[ru] ?? ru

/**
 * «Город (страна)» командировки. Форма шлёт в destinationCountryCode GUID страны 1С (strana_id):
 * его называем по справочнику направлений, а без справочника (мета списка) опускаем — сырой GUID
 * человеку не показываем. ISO легаси-заявок («US», «AZE») остаётся как был.
 */
export function tripDestination(p: Partial<BusinessTripPayload>, dests?: TripDestination[]): string {
  const code = String(p.destinationCountryCode ?? '').trim()
  const country = /^[a-z]{2,3}$/i.test(code) ? code : dests?.find((d) => d.stranaId === code)?.country
  return [p.destinationCityName, country && `(${country})`].filter(Boolean).join(' ')
}

/**
 * Human meta line from a request payload (best-effort; payload shape varies by type).
 * Возвращает {ru,en,az}: переводимые слова локализуются, данные (даты/имена/города) — нет.
 * `t()` выбирает az при активном языке; для ru/en/ka используется ru/en как прежде.
 */
export function metaOf(type: EdoType, payload: unknown): Localized {
  const p = (payload ?? {}) as Record<string, unknown>
  if (type === 'VACATION') {
    const k = VAC_KIND[String(p.kind)] ?? { ru: 'Отпуск', en: 'Vacation' }
    const range = `${ymd10(p.startDate as string)} – ${ymd10(p.endDate as string)}`
    return { ru: `${k.ru} · ${range}`, en: `${k.en} · ${range}`, az: `${azw(k.ru)} · ${range}` }
  }
  if (type === 'TIME_OFF') {
    // New contract (ТЗ-2026-002): timeFrom/timeTo/durationHours/absenceReason.
    const dur = p.durationHours != null ? fmtNum(Number(p.durationHours)) : ''
    const day = ymd10(p.timeFrom as string)
    const span =
      hhmm(p.timeFrom as string) && hhmm(p.timeTo as string)
        ? `${hhmm(p.timeFrom as string)}–${hhmm(p.timeTo as string)}`
        : ''
    const when = [day, span].filter(Boolean).join(' ')
    const reasonRaw = p.absenceReason ? String(p.absenceReason) : ''
    const reason = reasonRaw ? ` · ${reasonRaw}` : ''
    return {
      ru: `${dur ? dur + ' ч · ' : ''}${when}${reason}`,
      en: `${dur ? dur + ' h · ' : ''}${when}${reason}`,
      az: `${dur ? dur + ' saat · ' : ''}${when}${reasonRaw ? ` · ${azw(reasonRaw)}` : ''}`,
    }
  }
  if (type === 'BUSINESS_TRIP') {
    // «NYC (US) · 2026-07-01 – 2026-07-05 · 2 чел.»; GUID страны 1С — только город
    const where = tripDestination(p as Partial<BusinessTripPayload>)
    const range = `${ymd10(p.dateFrom as string)} – ${ymd10(p.dateTo as string)}`
    const n = Array.isArray(p.employeeIds) ? p.employeeIds.length : 1
    const group = n > 1 ? { ru: ` · ${n} чел.`, en: ` · ${n} ppl`, az: ` · ${n} nəfər` } : { ru: '', en: '', az: '' }
    return { ru: `${where} · ${range}${group.ru}`, en: `${where} · ${range}${group.en}`, az: `${where} · ${range}${group.az}` }
  }
  if (type === 'STUDY_LEAVE') {
    const range = `${ymd10(p.dateFrom as string)} – ${ymd10(p.dateTo as string)}`
    const uni = p.universityName ? ` · ${String(p.universityName)}` : ''
    return { ru: `Учебный отпуск · ${range}${uni}`, en: `Study leave · ${range}${uni}`, az: `${azw('Учебный отпуск')} · ${range}${uni}` }
  }
  if (type === 'HIRE') {
    const who = `${p.firstName ?? ''} ${p.lastName ?? ''}`.trim() || String((p.candidate as { fullName?: string })?.fullName ?? '')
    const position = (p.staffPosition as { name?: string })?.name ?? p.position
    const direct = p.hireOrigin === 'DIRECT'
    return { ru: `${direct ? 'Без подбора · ' : ''}${position ?? 'Приём'} · ${who}`, en: `${direct ? 'Without recruitment · ' : ''}${position ?? 'New hire'} · ${who}`, az: `${direct ? 'Seçimsiz · ' : ''}${position ?? azw('Приём')} · ${who}` }
  }
  if (type === 'TERMINATION') {
    const d = ymd10(p.terminationDate as string)
    return { ru: `Увольнение · ${d}`, en: `Termination · ${d}`, az: `${azw('Увольнение')} · ${d}` }
  }
  if (type === 'SICK_LEAVE') {
    const range = `${ymd10(p.dateFrom as string)} – ${ymd10(p.dateTo as string)}`
    const num = p.sheetNumber ? ` · №${String(p.sheetNumber)}` : ''
    return { ru: `Больничный · ${range}${num}`, en: `Sick leave · ${range}${num}`, az: `${azw('Больничный')} · ${range}${num}` }
  }
  if (type === 'PATERNITY_LEAVE' || type === 'MATERNITY_LEAVE' || type === 'CHILD_CARE_LEAVE') {
    // Вид уже в заголовке карточки (titleOf) → в мете только период отсутствия. Уход за ребёнком
    // может быть бессрочным (ТЗ-2026-013 §4.1): dateTo = null → «бессрочно» вместо второй даты.
    const from = ymd10(p.dateFrom as string)
    const to = ymd10(p.dateTo as string)
    const open = { ru: 'бессрочно', en: 'open-ended', az: 'müddətsiz' }
    const child = (p.child ?? {}) as Record<string, unknown>
    const who = child.firstName ? ` · ${[child.lastName, child.firstName].filter(Boolean).join(' ')}` : ''
    return {
      ru: `${from} – ${to || open.ru}${who}`,
      en: `${from} – ${to || open.en}${who}`,
      az: `${from} – ${to || open.az}${who}`,
    }
  }
  if (type === 'RECRUITMENT') {
    // String() как у gradeRaw/deptRaw рядом: не-строка в payload не должна дать [object Object].
    const titleRaw = p.jobTitle ? String(p.jobTitle) : ''
    const gradeRaw = p.jobGradeNameRu ? String(p.jobGradeNameRu) : ''
    const deptRaw = p.department ? String(p.department) : ''
    const grade = gradeRaw ? ` · ${gradeRaw}` : ''
    const dept = deptRaw ? ` · ${deptRaw}` : ''
    return {
      ru: `${titleRaw || 'Вакансия'}${grade}${dept}`,
      en: `${titleRaw || 'Vacancy'}${grade}${dept}`,
      az: `${titleRaw || azw('Вакансия')}${gradeRaw ? ` · ${azw(gradeRaw)}` : ''}${deptRaw ? ` · ${azw(deptRaw)}` : ''}`,
    }
  }
  if (type === 'FREEFORM') {
    // Тема — свободный текст пользователя, не переводим. Длина маршрута — сколько согласующих.
    const title = p.title ? String(p.title) : ''
    const n = Array.isArray(p.approvers) ? p.approvers.length : 0
    const route = n
      ? { ru: ` · ${n} согл.`, en: ` · ${n} approvers`, az: ` · ${n} razılaşdıran` }
      : { ru: '', en: '', az: '' }
    return { ru: `${title}${route.ru}`, en: `${title}${route.en}`, az: `${title}${route.az}` }
  }
  if (type === 'IT_WORKS') {
    // «Сентябрь 2026 · 1250 AZN». Подрядчика в мете нет: payload строки списка несёт только contractorId,
    // имя отдаёт лишь карточка (card.itWorks.contractor). Черновик неполный — пустые части пропускаем.
    const amount = typeof p.amount === 'number' ? `${fmtNum(p.amount, 2)} ${p.currency ?? ''}`.trim() : ''
    const m = (lang: Lang) => [p.period ? periodLabel(String(p.period), lang) : '', amount].filter(Boolean).join(' · ')
    return { ru: m('ru'), en: m('en'), az: m('az') }
  }
  return { ru: '', en: '' }
}

/** EN-варианты для типов со своей меткой — EDO_TYPE_LABEL (src/lib/edo-labels.ts) даёт только ru. */
const OWN_TITLE_EN: Partial<Record<EdoType, string>> = {
  STUDY_LEAVE: 'Study leave',
  SICK_LEAVE: 'Sick leave',
  PATERNITY_LEAVE: 'Paternity leave',
  MATERNITY_LEAVE: 'Maternity leave',
  CHILD_CARE_LEAVE: 'Child-care leave',
}

/** Стороны договора для строки списка: наша компания и контрагент. У прочих типов — пусто. */
export function partiesOf(type: EdoType | undefined, payload: unknown): Pick<ProcVM, 'company' | 'counterparty'> {
  if (type !== 'CONTRACT') return {}
  const p = (payload ?? {}) as Record<string, unknown>
  const code = p.company ? String(p.company) : ''
  return {
    company: code ? (CONTRACT_ENUM_LABEL[code] ?? { ru: code, en: code }) : undefined,
    counterparty: p.counterpartyName ? String(p.counterpartyName) : undefined,
  }
}

const groupLabel = (type: EdoType): Localized => typeOf(TYPE_OF[type]).name

/**
 * Конкретное наименование документа для заголовка карточки списка (ProcessRow, components/ProcessRow.tsx):
 * VACATION → вид отпуска (VAC_KIND по payload.kind, фолбэк — метка группы, как у metaOf); типы со
 * своей меткой, отличной от группы (STUDY_LEAVE/SICK_LEAVE/*_LEAVE — см. OWN_TITLE_EN) →
 * EDO_TYPE_LABEL[type]; остальные — метка группы (то, что и так рисовалось верхней строкой).
 */
export function titleOf(type: EdoType, payload: unknown): Localized {
  if (type === 'VACATION') {
    const p = (payload ?? {}) as Record<string, unknown>
    return VAC_KIND[String(p.kind)] ?? groupLabel(type)
  }
  const en = OWN_TITLE_EN[type]
  return en ? { ru: EDO_TYPE_LABEL[type], en } : groupLabel(type)
}

const actOf = (stage: EdoStage): Localized => STAGE_ACT[stage] ?? { ru: stage, en: stage }
const groupOf = (g?: string | null): Localized | undefined =>
  g ? (GROUP_LABEL[g] ?? { ru: g, en: g }) : undefined

/**
 * Маршрут согласования для таймлайна. `serverRoute` (GET /edo/requests/:id → route[]) —
 * ПОЛНЫЙ маршрут: пройденные шаги + текущий + ещё не созданные, виден на любом статусе.
 * Фолбэк по tasks[] остаётся для строк списка (`/edo/requests`, `/edo/tasks`), где сервер
 * маршрут не отдаёт: там видно только пройденное, но карточка и дровер грузят полный.
 */
export function routeOf(tasks?: EdoTask[], serverRoute?: EdoRouteStep[]): RouteStep[] {
  if (serverRoute?.length) {
    return serverRoute.map((r) => ({
      act: actOf(r.stage),
      who: '',
      state: r.state,
      when: r.decidedAt ? timeAgo(r.decidedAt) : undefined,
      person: r.person ? { id: r.person.id, name: r.person.fullName, position: r.person.jobTitle } : undefined,
      // Группу показываем, только если персоны нет (иначе дублировали бы ответственного).
      group: r.person ? undefined : groupOf(r.group),
      // Кандидаты приходят только в предпросмотре маршрута (до подачи заявки).
      candidates: r.candidates?.map((c) => ({ id: c.id, name: c.fullName })),
      comment: r.comment,
    }))
  }
  if (!tasks?.length) return [{ act: { ru: 'Подача заявки', en: 'Request submitted' }, who: '', state: 'done' }]
  return tasks.map((tk) => {
    // Ответственный: кто фактически решил (для завершённых) → иначе текущий адресат.
    const p = tk.decisionBy ?? tk.assignee
    const person = p ? { id: p.id, name: p.fullName, position: p.jobTitle } : undefined
    // Групповая задача без адресата — показываем название группы («Отдел кадров» и т.п.).
    const group = !person && tk.candidateGroup ? GROUP_LABEL[tk.candidateGroup] : undefined
    return {
      act: actOf(tk.stage),
      who: '',
      state: TASK_STATE[String(tk.status)] ?? 'pending',
      when: tk.decidedAt ? timeAgo(tk.decidedAt) : undefined,
      person,
      group,
      comment: tk.decisionComment ?? undefined,
    }
  })
}

/**
 * Индекс шага, на котором документ стоит сейчас: текущий; иначе отклонённый, ПОСЛЕ которого
 * маршрут дальше не пошёл (в хвосте только pending/na). 'rejected' — это и отказ, и возврат на
 * доработку (TASK_STATE): после доработки документ идёт дальше, и тот шаг — уже история.
 * Нет ни того ни другого → -1.
 * ponytail: порядок шагов = порядок создания задач. В параллельном круге отказ того, чья задача
 * создана раньше уже согласовавшего, точкой остановки не станет (на стендах 29.09.2026 таких
 * заявок нет). Понадобится — отличать отказ от возврата по статусу задачи, а не по позиции.
 */
const activeIndex = (route: RouteStep[]) => {
  const cur = route.findIndex((r) => r.state === 'current')
  if (cur >= 0) return cur
  return route.findIndex(
    (r, i) =>
      r.state === 'rejected' &&
      !route.slice(i + 1).some((n) => n.state === 'done' || n.state === 'current' || n.state === 'skipped'),
  )
}

/** Позиция документа в маршруте: сколько шагов уже позади + текущий (для «шаг N из M»). */
export const stepOf = (route: RouteStep[]) => {
  const at = activeIndex(route)
  if (at >= 0) return at + 1
  const passed = route.filter((r) => r.state !== 'pending').length
  return Math.max(1, passed)
}

/**
 * Номер документа для показа: «DOC-0042» — формат из макета (демо-строки уже так и выглядят).
 * Последовательность сквозная по всем типам процессов и не обнуляется по годам, поэтому в номере
 * нет ни года, ни кода типа: тип и дата и так на карточке. Ширина 4 — косметика для выравнивания
 * списка, на 10000-й заявке номер просто станет длиннее.
 * Демо-строки номера не имеют — у них id и есть номер («DOC-1042»).
 * IT-работы — свой номер с подачи (ITO-2026-000128, им же подписаны уведомления и протокол); у
 * черновика его ещё нет — прежний формат. Другие типы с portalRequestId (договор) — как были.
 */
export const docNo = (p: Pick<ProcVM, 'docNumber' | 'id'> & Partial<Pick<ProcVM, 'type' | 'portalRequestId'>>): string =>
  p.type === TYPE_OF.IT_WORKS && p.portalRequestId
    ? p.portalRequestId
    : p.docNumber ? `DOC-${String(p.docNumber).padStart(4, '0')}` : p.id

/**
 * IT_WORKS: «шаг N из 2» — по этапам плана (модули → финал), одинаково в строке списка и в итоге
 * карточки. Серверный маршрут разворачивает этап модулей в задачу на каждого согласующего, а
 * доработку — в новый проход, поэтому счёт по его строкам дал бы «1 из 3» при двух согласующих и
 * рос бы с каждым раундом; у строки списка маршрута нет вовсе («1 из 1»). Этап — по последней
 * задаче (задачи приходят по createdAt): финал → 2; модули и доработка у инициатора → 1 (после
 * переотправки документ снова идёт по маршруту). Входящая задача — по этапу её самой.
 */
export const IT_WORKS_STEPS = 2
export const itWorksStep = (tasks?: Pick<EdoTask, 'stage'>[]): number =>
  tasks?.[tasks.length - 1]?.stage === 'IT_FINAL' ? 2 : 1

/**
 * Маршрут карточки для показа (вкладка «Маршрут», лист согласования). IT_WORKS: будущий шаг финала
 * сервер отдаёт группой `it_final` без персоны — персону он ставит только созданным задачам, — а
 * финальный утверждающий заявки известен (`itWorks.finalApprover`). Должности в нём нет — не
 * показываем. Шаг IT_FINAL есть только у IT_WORKS, другим типам маршрут возвращается как есть.
 */
export function cardRoute(card: Pick<RequestCard, 'route' | 'itWorks'>): EdoRouteStep[] | undefined {
  const fa = card.itWorks?.finalApprover
  if (!fa) return card.route
  return card.route?.map((r) =>
    r.stage === 'IT_FINAL' && !r.person ? { ...r, person: { id: fa.id, fullName: fa.fullName, jobTitle: null } } : r,
  )
}

/**
 * Флаги моей задачи по документу (taskId/stage/canDecide) — из ЖИВОГО списка «Ждут моего решения»
 * (GET /edo/tasks). Строка списка — снимок на момент клика: дровер держит её, а задача после
 * решения из «Входящих» уходит (IT_WORKS: после решения по строкам дровер не закрывается, и шапка
 * оставалась «Ждёт вашего решения»). Задача строки ещё открыта — она; закрыта, но у меня следующая
 * задача по документу (финал у того же человека) — она; иначе решать нечего. Список не загружен —
 * снимок как есть. Тем же путём VM собирает страница /processes/[id].
 * `cardTasks` — задачи живой карточки: она приходит раньше (ответ решения кладётся в кэш сразу),
 * а GET /edo/tasks перечитывается долго. Задача, которая в карточке уже не OPEN, — не моя, что бы
 * ни держали отставшие «Входящие» (иначе до их ответа шапка мигала прежним «Ждёт вашего решения»).
 */
export function withMyTask(vm: ProcVM, myTasks: EdoTask[] | undefined, cardTasks?: EdoTask[]): ProcVM {
  if (!vm.reqId) return vm
  const closed = new Set((cardTasks ?? []).filter((tk) => String(tk.status) !== 'OPEN').map((tk) => tk.id))
  const none = { ...vm, taskId: undefined, stage: undefined, canDecide: false }
  if (!myTasks) return vm.taskId && closed.has(vm.taskId) ? none : vm
  const mine = myTasks.filter((tk) => (tk.requestId ?? tk.request?.id) === vm.reqId && !closed.has(tk.id))
  const tk = mine.find((x) => x.id === vm.taskId) ?? mine[0]
  return tk ? { ...vm, taskId: tk.id, stage: tk.stage, canDecide: true } : none
}

const baseReqVM = (
  req: EdoRequest & { myRoles?: EdoParticipantRole[]; route?: EdoRouteStep[] },
): ProcVM => {
  const preparedTermination = isLocalTerminationDraft(req)
  const route = preparedTermination ? [] : routeOf(req.tasks, req.route)
  const status = preparedTermination ? 'work' : STATUS_OF(req.status)
  return {
    id: req.id,
    ...(preparedTermination ? { approvalRouteUnavailable: true } : {}),
    docNumber: req.number,
    portalRequestId: req.portalRequestId,
    type: TYPE_OF[req.type] ?? 'unknown',
    status,
    statusLabel: preparedTermination ? { ru: 'Черновик', en: 'Draft' } : req.status === 'REWORK' ? { ru: 'На доработке', en: 'Rework' } : undefined,
    meta: metaOf(req.type, req.payload),
    title: titleOf(req.type, req.payload),
    // Завершённый документ прошёл маршрут целиком — полоска полная, что бы ни осталось в хвосте.
    step: status === 'done' ? route.length : stepOf(route),
    steps: route.length,
    when: timeAgo(req.createdAt),
    // «С моим участием» (myRoles задан): «моя», если я инициатор или подал «от имени» — обоим
    // доступно «Напомнить»/«Отозвать». Путь myRequests (без myRoles) — инициатор либо подавший → mine.
    mine: req.myRoles
      ? req.myRoles.includes('initiator') || req.myRoles.includes('submitter')
      : true,
    task: false,
    route,
    live: true,
    reqId: req.id,
    employmentCompany: req.employment?.companyName,
    myRoles: req.myRoles,
    author: req.initiator?.fullName,
    ...partiesOf(req.type, req.payload),
    // Пометка «от имени: ФИО» у заявок proxy (в «Моих заявках» бэк отдаёт initiator).
    onBehalfOf: req.submittedById ? req.initiator?.fullName : undefined,
    // Напоминать некому на любом терминале (закрыта/отменена/зарегистрирована — бэк вернёт ошибку).
    // На шаге автора договора — тоже: задача у самого автора. Пока договор передаётся в 1С — тоже.
    canRemind: !['APPROVED', 'POSTED', 'REJECTED', 'CLOSED', 'CANCELLED', 'REGISTERED', 'AWAITING_SIGNED_COPY', 'POSTING', 'AWAITING_TERMINATION_POSTING'].includes(req.status) && !(req.type === 'TERMINATION' && req.status === 'DRAFT'),
  }
}

/**
 * Черновик IT_WORKS до первой подачи: процесса, задач и маршрута ещё нет — не «На согласовании»
 * с «Подача заявки ✓», а «Черновик» без маршрута; напоминать некому (сервер ответил бы 404).
 * Отдельной обёрткой над baseReqVM, чтобы не зависеть от ветки черновика увольнения (её нет в main).
 */
export const reqVM = (req: EdoRequest & { myRoles?: EdoParticipantRole[]; route?: EdoRouteStep[] }): ProcVM => {
  const vm = baseReqVM(req)
  if (req.type !== 'IT_WORKS') return vm
  // поданная: «шаг N из 2» по этапам плана (см. itWorksStep), завершённая — полная полоска
  if (req.status !== 'DRAFT') return { ...vm, step: vm.status === 'done' ? IT_WORKS_STEPS : itWorksStep(req.tasks), steps: IT_WORKS_STEPS }
  return {
    ...vm,
    status: 'work',
    statusLabel: { ru: 'Черновик', en: 'Draft', az: 'Qaralama' },
    approvalRouteUnavailable: true,
    route: [],
    step: 1,
    steps: 0,
    canRemind: false,
  }
}

export const taskVM = (tk: EdoTask): ProcVM => {
  const req = tk.request
  return {
    id: tk.id,
    // Номер берём у заявки, а не у задачи: во «Входящих» показываем номер документа, который
    // согласуют. Раньше сюда попадал хвост cuid самой задачи — т.е. чужой «номер».
    docNumber: req?.number,
    portalRequestId: req?.portalRequestId,
    type: req ? (TYPE_OF[req.type] ?? 'unknown') : 'unknown',
    status: 'review',
    meta: req ? metaOf(req.type, req.payload) : { ru: '', en: '' },
    title: req ? titleOf(req.type, req.payload) : typeOf('unknown').name,
    // IT_WORKS — этап задачи в плане из двух (как в шапке карточки); прочие — «1 из 1», как было
    ...(req?.type === 'IT_WORKS' ? { step: itWorksStep([tk]), steps: IT_WORKS_STEPS } : { step: 1, steps: 1 }),
    when: '',
    mine: false,
    task: true,
    route: [{ act: STAGE_ACT[tk.stage] ?? { ru: tk.stage, en: tk.stage }, who: '', state: 'current' }],
    live: true,
    reqId: req?.id,
    employmentCompany: req?.employment?.companyName,
    author: req?.initiator?.fullName,
    ...partiesOf(req?.type, req?.payload),
    taskId: tk.id,
    stage: tk.stage,
    canDecide: true, // /edo/tasks returns only OPEN tasks actionable by me
  }
}

/**
 * Действия по открытой задаче в дровере: взять/вернуть групповую (claim/unclaim) и признак
 * «занята другим». Чистая функция — тестируется отдельно от UI. `openTask` — живая открытая
 * задача из карточки (GET /edo/requests/:id); `meId`/`initiatorId` — из /users/me и карточки;
 * `canDecide` — из VM (только для входящих задач согласующего).
 *
 * Групповая задача (`candidateGroup`): пока не взята — любой член группы может решать или взять;
 * взял другой (`assigneeId` ≠ я) — бэк вернёт 403 на decide, поэтому прячем и решения, и «Взять».
 * Инициатор свою заявку не согласует. Персональная задача (без группы) — только у назначенного
 * или у его заместителя на время отпуска (`actingFor` карточки): решать — да, вернуть в группу — нет.
 */
export function taskActions(opts: {
  openTask?: Pick<EdoTask, 'assigneeId' | 'candidateGroup'> | null
  meId?: string
  initiatorId?: string
  /** Подавший «от имени» (роль proxy) — задача инициатора-сотрудника для него не «чужая». */
  submittedById?: string | null
  /** Кого я замещаю (actingFor карточки) — их задачи для меня не «чужие». */
  actingFor?: string[]
  /**
   * `detail.canClaim` карточки: сервер берёт только настоящего носителя роли — заместитель по
   * отпуску невзятую групповую задачу решает, но не берёт (иначе «Взять» → 403). `false` прячет
   * «Взять в работу»; нет поля (старый бэк) — как раньше.
   */
  claimAllowed?: boolean
  canDecide?: boolean
}): { canClaim: boolean; canUnclaim: boolean; assignedToOther: boolean } {
  const { openTask, meId, initiatorId, canDecide } = opts
  const isGroup = !!openTask?.candidateGroup
  const assigneeId = openTask?.assigneeId ?? null
  const iAmInitiator = !!meId && !!initiatorId && initiatorId === meId
  // Доработка заявки, поданной мной за сотрудника: назначена на него, закрываю её я (бэк разрешает).
  const proxyForAssignee =
    !!meId && opts.submittedById === meId && !!assigneeId && assigneeId === initiatorId
  const actingForAssignee = !!assigneeId && !!opts.actingFor?.includes(assigneeId)
  const assignedToOther =
    !!meId && !!assigneeId && assigneeId !== meId && !proxyForAssignee && !actingForAssignee
  const canClaim = !!canDecide && isGroup && !assigneeId && !iAmInitiator && opts.claimAllowed !== false
  const canUnclaim = !!canDecide && isGroup && !!assigneeId && assigneeId === meId
  return { canClaim, canUnclaim, assignedToOther }
}

/**
 * Добавить согласующего в идущий документ — куда именно, решает бэк (`detail.addApproverSlot`,
 * тот же insertionIndex, что и POST /edo/requests/:id/approvers): null/нет поля — добавлять
 * некуда. Клиент добавляет только проверку «я участник» — инициатор/подавший «от имени» или
 * адресат ОТКРЫТОЙ задачи (в т.ч. второй открытой задачи параллельного круга). Старый бэк без
 * поля addApproverSlot → can: false (безопасно: бэк деплоится первым).
 */
export function addApproverAccess(
  detail: RequestCard | undefined,
  meId: string | undefined,
): { can: boolean; beforeFinal: boolean } {
  const beforeFinal = !!detail?.addApproverSlot?.beforeFinal
  if (!detail || !meId || !detail.addApproverSlot) return { can: false, beforeFinal }
  const isParticipant =
    meId === detail.initiator?.id ||
    meId === detail.submittedBy?.id ||
    detail.tasks.some((tk) => String(tk.status) === 'OPEN' && tk.assigneeId === meId)
  return { can: isParticipant, beforeFinal }
}

export const currentStep = (p: Pick<Process, 'route'>): RouteStep | undefined => p.route[activeIndex(p.route)]

/** Tally a VM list into status → count (feeds the status summary cards). */
export function statusCounts(vms: ProcVM[]): Record<string, number> {
  const counts: Record<string, number> = {}
  for (const p of vms) counts[p.status] = (counts[p.status] ?? 0) + 1
  return counts
}

/**
 * Склейка списков по документу (reqId): строки `lead` (задачи — с действием) идут как есть и
 * вытесняют из `rest` строки того же документа; в `rest` остаётся первая строка документа
 * (инициированная заявка раньше своего дубля из participating — с mine=true и «Напомнить»).
 */
export function mergeByDoc(lead: ProcVM[], rest: ProcVM[]): ProcVM[] {
  const seen = new Set(lead.map((v) => v.reqId).filter(Boolean))
  return [
    ...lead,
    ...rest.filter((v) => {
      const key = v.reqId ?? v.id
      if (seen.has(key)) return false
      seen.add(key)
      return true
    }),
  ]
}

/**
 * «Согласую / подписываю» — документы, где я участник, но не автор: открытые задачи и участие
 * минус мои заявки (и задачи по ним — доработка, скан договора), склеенные по документу.
 */
export function signingOf(tasks: ProcVM[], reqs: ProcVM[], participating: ProcVM[]): ProcVM[] {
  const mine = new Set(reqs.map((v) => v.reqId ?? v.id))
  const notMine = (v: ProcVM) => !v.mine && !mine.has(v.reqId ?? v.id)
  return mergeByDoc(tasks.filter(notMine), participating.filter(notMine))
}

/**
 * Кем я прихожусь документу — метка роли на строке. Задача: подпись → подписант, доработка и
 * скан подписанного договора — шаги автора, прочее → согласующий. Иначе — роли из participating,
 * а у строки из «Моих заявок» (ролей бэк не отдаёт) — автор.
 */
export function rolesOf(p: ProcVM): EdoParticipantRole[] {
  if (p.task) return [p.stage === 'SIGNATURE' ? 'signer' : p.stage === 'REWORK' || p.stage === 'SIGNED_COPY' ? 'initiator' : 'approver']
  if (p.myRoles?.length) return p.myRoles
  return p.mine ? ['initiator'] : []
}

/**
 * Поиск по списку: название, вид, № документа, автор, детали (meta), стороны договора — на всех
 * языках сразу и в текущем (`t`: az-переводы из словаря), без регистра и диакритики.
 */
export function matchesQuery(p: ProcVM, query: string, t: (v?: Localized) => string): boolean {
  const q = fold(query.trim())
  if (!q) return true
  const type = typeOf(p.type).name
  const hay = [p.title, type, p.meta, p.company].flatMap((v) => [allLangs(v), t(v)])
  return fold([...hay, docNo(p), p.author, p.counterparty, p.employmentCompany].join(' ')).includes(q)
}
